import { Type } from 'shared/utils/flowstep';
import { mappingToFieldTypes } from 'shared/excel/runtime/flowstepExecution';
import { applyInsertMapping } from 'shared/excel/runtime/insert';
import { ConversionError, NamesConverter } from 'shared/excel/converters';
import { PushFlowstep } from 'src/services/runtime/pushFlowstep';
import { pick } from 'shared/utils/utils';


export class InsertFlowstep extends PushFlowstep {
  constructor(flowstep, meta, parent) {
    super(flowstep, meta, parent);

    this.conversion = {
      errors: [],
      objectsToReject: [],
      currentRowIndexInData: 0,
      spansTreeToReject: null,
      namesConverter: new NamesConverter(this.loaders),
    };

    this.applyFnContext = {
      namesConverter: this.conversion.namesConverter,
      loaders: this.loaders,
    };
  }

  // override PushFlowstep
  get disableFilters() { return false; }
  get isUpdate() { return Type.Update === this.flowstep.type; }

  // override PushFlowstep
  async _processChunk_(values) {
    const chunkDataSourceTree = this.dataSourceTree.pickCount(values.length);

    // console.log('Insert::processChunk: ', chunkDataSourceTree);
    // console.log('Values: ', values);

    const insertTree = (tree, values, insertIdsInDataTable) => {
      if (tree.isEmpty()) {
        return;
      }

      // console.log('Inserting tree: ', tree, values, insertIdsInDataTable);

      for (const rangeVisibleTree of tree.children) {
        const rangeValues = values.splice(0, rangeVisibleTree.getValuesCount());
        const { index, count } = rangeVisibleTree;

        if (this.showResultsTable) {
          this.resultsTable
            .getRows(index, count)
            .getVisibleView()
            .values = rangeValues;
        }

        if (insertIdsInDataTable) {
          const ids = rangeValues.map(row => [this.getResultId(row)]);
          this.dataTable
            .getRows(index, count)
            .getColumn(this.columnIndexForResultIds)
            .getVisibleView()
            .values = ids;
        }
      }
    }

    // Consider results with empty id invalid as well
    const criteria = (row) => !this.getResultId(row) ? this.getResultStatus(row) : this.errorMapper(row);
    const { resultErrorsOrEmpty, hasErrors } = this.collectResultErrors(values, chunkDataSourceTree, criteria);

    if (this.isUpdate || this.columnIndexForResultIds === null) { // In this case we don't care about overwriting ids with empty ids from response
      // console.log('Writing the whole tree without ids');
      insertTree(chunkDataSourceTree, values, false); // Write results only
    }
    else {
      // console.log('Deciding: hasErrors: ', hasErrors);
      // console.log('Criterias: ', criteriasWithoutErrors);

      if (!hasErrors) { // If all entries don't contain errors, write results + ids in data table
        insertTree(chunkDataSourceTree, values, true);
      }
      else { // Ohterwise split results depending on having or not having errors
        const valuesWithoutErrors = values.filter((x, i) => !resultErrorsOrEmpty[i]);
        const valuesWithErrors = values.filter((x, i) => !!resultErrorsOrEmpty[i]);
        const { toLeft: treeWithoutErrors, toPick: treeWithErrors } = chunkDataSourceTree.pickByCriteria(resultErrorsOrEmpty);

        insertTree(treeWithoutErrors, valuesWithoutErrors, true); // Write results + ids
        insertTree(treeWithErrors, valuesWithErrors, false); // Write results only
      }
    }
  }

  // override Flowstep
  async _applyFlowstepData_(excelHeaders) {
    const { options } = this.flowstep;
    const { batchSize, parallelCount, runSynchronously, bypassDuplicatesCheck } = options;
    const { sfObjectName: objectName, externalIdFieldName, ownerChangeOptions = [] } = options.mapping;

    
    const { constants, mapping, columnIndexForResultIds, upsertRelationships, columnIndexes, processRow } = 
      await applyInsertMapping(excelHeaders, options.mapping, this.applyFnContext);

    // console.log('Apply mapping. Constants: ', constants);

    this.columnIndexForResultIds = this.isUpdate ? null : columnIndexForResultIds;
    this.requestContext = { columnIndexes, processRow };
    this.options = { objectName, externalIdFieldName, batchSize, runSynchronously, parallelCount, upsertRelationships, constants, ownerChangeOptions, bypassDuplicatesCheck };

    return mappingToFieldTypes(mapping);
  }

  // override PushFlowstep
  _loadValues_(range, rangeTree) {
    const { columnIndexes } = this.requestContext;
    const mappedColumns = columnIndexes.map(columnIndex => {
      const view = range
        .getColumn(columnIndex)
        .getVisibleView()
        .load(['values']);

      return { view };
    });

    return { mappedColumns, rangeTree };
  }

  // override PushFlowstep
  _mapValues_(loaded) {
    const { rangeTree, mappedColumns } = loaded;
    const { processRow } = this.requestContext;

    return mappedColumns[0].view.values.map((unused, rowIndex) => {
      const rowIndexInData = this.conversion.currentRowIndexInData++;

      const row = mappedColumns.reduce((out, { view }) => {
        const value = view.values[rowIndex][0];
        out.push(value);
        return out;
      }, []);

      const { object, errors } = processRow(row, rowIndexInData);
      
      errors.forEach(({ error, columnIndex }) => 
        this.conversion.errors.push({
          error,
          sheet: this.dataTable.worksheet.name,
          // columnIndex is a column number in the data table
          // rowIndex is a row number in this batch, which is described in rangeTree. 
          // So, first we need to convert it to a relative row indexes of the whole table
          ...this.dataTable.toAbsoluteIndexes({ rowIndex: rangeTree.getIndexAtPosition(rowIndex), columnIndex })
        })
      );
      
      return object;
    });
  }

  // override Flowstep
  async _buildRequest_(initialObjects) {
    const { errors, namesConverter } = this.conversion;
    if (errors.length) {
      // console.log('ConversionErrors: ', errors);
      throw new ConversionError(errors);
    }

    const res = await namesConverter.replaceNamesWithIds(this.dataSourceTree, initialObjects);
    const { objectsToReject, spansTreeToReject, objectsToUpload: objects, spansTreeToUpload } = res;

    Object.assign(this.conversion, { objectsToReject, spansTreeToReject });
    this.dataSourceTree = spansTreeToUpload;

    return { objects, options: this.options };
  }

  // override PushFlowstep (optional)
  async _preInsertResults_(timestamp) {
    const { spansTreeToReject, objectsToReject } = this.conversion;

    // rowIndex should be relative to the beginning of the data table
    this.resultErrors.push(...objectsToReject.map(({ error }, index) =>  ({
      error, rowIndex: spansTreeToReject.getIndexAtPosition(index)
    })));
    
    if (!this.showResultsTable || !spansTreeToReject) { 
      return;
    }

    for (const { index, count } of spansTreeToReject.children) {
      const objects = objectsToReject.splice(0, count);
      const values = NamesConverter.errorsToValues(objects, timestamp, this.resultTableHeaders);
      
      this.resultsTable
        .getRows(index, count)
        .getVisibleView()
        .values = values;
    }

    await this.sync();
  }
};